// Job pipeline W5 (client half): hydrate the artifact-referenced raytrace/
// modal-surface wire forms `analyze_scene` and placement-candidate `viz`
// return (`RaytraceRefResponse`/`ModalRefResponse`, see adminTypes.ts) back
// into the EXACT inline shapes (`RaytraceResponse`/`ModalResponse`) every
// existing consumer already reads — `RaytraceCloud.tsx`, `SurfaceEnergyMap.tsx`,
// `reflectionsFilter.ts`, `echogramDraw.ts`, `ModalHeatmap.tsx`, and the
// packages/charts echogram geometry all index `positions`/`energies`/
// `distances`/`bounces`/`speaker_idx` as plain arrays and `surfaces[key].grid`
// as `grid[u][v]`. Neither shape's consumers should learn about `ArtifactRef`
// — this is the one seam that decodes it, mirroring `resolveVolumeBytes`
// (the same pattern for the volume-sweep/velocity cube).
//
// Legacy inline responses resolve synchronously (still wrapped in a Promise
// for a uniform call site); ref-form responses fetch every referenced
// artifact concurrently.
import type {
  ArtifactRef,
  ModalResponse,
  ModalRefResponse,
  ModalSurface,
  RaytraceResponse,
  RaytraceRefResponse,
  Vec3,
} from '@roomtreatment/core';
import { isModalRef, isRaytraceRef } from '@roomtreatment/core';

export type FetchArtifactFn = (sha: string, signal?: AbortSignal) => Promise<Uint8Array>;

function toTypedArray(bytes: Uint8Array, dtype: ArtifactRef['dtype']): Float32Array | Uint8Array {
  if (dtype === 'uint8') return bytes;
  // `bytes` comes back from `fetchArtifact` as a freshly-allocated buffer
  // (the decompressed artifact), so byteOffset is 0 and this reinterpret is
  // safe without a copy.
  return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength >> 2);
}

async function fetchAsNumberArray(
  ref: ArtifactRef,
  fetchArtifact: FetchArtifactFn,
): Promise<number[]> {
  const bytes = await fetchArtifact(ref.artifact);
  return Array.from(toTypedArray(bytes, ref.dtype));
}

async function fetchAsVec3Array(
  ref: ArtifactRef,
  fetchArtifact: FetchArtifactFn,
): Promise<Vec3[]> {
  const bytes = await fetchArtifact(ref.artifact);
  const flat = toTypedArray(bytes, ref.dtype);
  const n = ref.shape[0] ?? Math.floor(flat.length / 3);
  const out: Vec3[] = new Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = [flat[i * 3]!, flat[i * 3 + 1]!, flat[i * 3 + 2]!];
  }
  return out;
}

/** Resolve one `analyze_scene`/placement-candidate raytrace field into the
 *  exact `RaytraceResponse` shape — inline responses pass through
 *  unchanged; ref-form responses fetch the five parallel arrays
 *  concurrently and reassemble them. */
export async function resolveRaytrace(
  resp: RaytraceResponse | RaytraceRefResponse,
  fetchArtifact: FetchArtifactFn,
): Promise<RaytraceResponse> {
  if (!isRaytraceRef(resp)) return resp;
  const { positions, energies, distances, bounces, speaker_idx: speakerIdx, ...meta } = resp;
  const [positionsOut, energiesOut, distancesOut, bouncesOut, speakerIdxOut] = await Promise.all([
    fetchAsVec3Array(positions, fetchArtifact),
    fetchAsNumberArray(energies, fetchArtifact),
    fetchAsNumberArray(distances, fetchArtifact),
    fetchAsNumberArray(bounces, fetchArtifact),
    fetchAsNumberArray(speakerIdx, fetchArtifact),
  ]);
  return {
    ...meta,
    positions: positionsOut,
    energies: energiesOut,
    distances: distancesOut,
    bounces: bouncesOut,
    speaker_idx: speakerIdxOut,
  };
}

/** Reshape a flat float array (row-major, [NU, NV]) into the nested
 *  `grid[u][v]` shape `ModalHeatmap.tsx` indexes — mirrors the backend's
 *  `np.ascontiguousarray` C-order flatten in `modal_surface_refs`. */
function reshapeGrid(flat: Float32Array | Uint8Array, shape: number[]): number[][] {
  const [nu, nv] = shape as [number, number];
  const out: number[][] = new Array(nu);
  for (let u = 0; u < nu; u++) {
    const row: number[] = new Array(nv);
    const base = u * nv;
    for (let v = 0; v < nv; v++) row[v] = flat[base + v]!;
    out[u] = row;
  }
  return out;
}

/** Resolve one `analyze_scene`/placement-candidate modal field into the
 *  exact `ModalResponse` shape — inline responses pass through unchanged;
 *  ref-form responses fetch every surface's grid concurrently. */
export async function resolveModal(
  resp: ModalResponse | ModalRefResponse,
  fetchArtifact: FetchArtifactFn,
): Promise<ModalResponse> {
  if (!isModalRef(resp)) return resp;
  const entries = Object.entries(resp.surfaces);
  const resolved = await Promise.all(entries.map(async ([key, surf]) => {
    if (!surf) return [key, surf] as const;
    const bytes = await fetchArtifact(surf.grid.artifact);
    const flat = toTypedArray(bytes, surf.grid.dtype);
    const { grid: _grid, ...surfMeta } = surf;
    const inline: ModalSurface = { ...surfMeta, grid: reshapeGrid(flat, surf.grid.shape) };
    return [key, inline] as const;
  }));
  return { ...resp, surfaces: Object.fromEntries(resolved) };
}
